import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck, ShieldOff, UserPlus, Plus, Search, Edit2, Trash2, History, AlertTriangle, CheckCircle2 } from "lucide-react";

const DoctorCommsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [intakeDoctors, setIntakeDoctors] = useState([]);
  const [consentRecords, setConsentRecords] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Doctor contact form
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ patient_id: "", doctor_name: "", doctor_email: "", doctor_phone: "", doctor_practice: "", notes: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [patients, setPatients] = useState([]);

  // Consent form
  const [showConsentForm, setShowConsentForm] = useState(null); // patient_id
  const [consentAction, setConsentAction] = useState("grant"); // grant or withdraw
  const [consentForm, setConsentForm] = useState({ consent_type: "doctor_sharing", scope: "", method: "verbal", notes: "", doctor_contact_id: "" });
  const [savingConsent, setSavingConsent] = useState(false);

  useEffect(() => { fetchAll(); fetchPatients(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, conRes] = await Promise.all([
        fetch(`${API}/staff/all-doctor-contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/staff/consent-records`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (cRes.ok) {
        const d = await cRes.json();
        setContacts(d.doctor_contacts || []);
        setIntakeDoctors(d.intake_doctor_info || []);
      }
      if (conRes.ok) {
        const d = await conRes.json();
        const all = [
          ...(d.intake_consents || []).map(c => ({ ...c, source: "Intake form", date: c.submitted_at, type: "intake" })),
          ...(d.assessment_consents || []).map(c => ({ ...c, source: `Assessment by ${c.staff_name}`, date: c.created_at, type: "assessment" })),
        ].sort((a, b) => new Date(b.date) - new Date(a.date));
        setConsentRecords(all);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPatients = async () => {
    try {
      const res = await fetch(`${API}/admin/users?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPatients((d.users || d).filter(u => u.role === "patient")); }
    } catch (e) { console.error(e); }
  };

  const fetchConsentHistory = async (patientId) => {
    try {
      const res = await fetch(`${API}/staff/patient/${patientId}/consent-history`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAuditTrail(d.audit_trail || []); setConsentRecords(prev => { /* merge dedicated records */ const dedicated = (d.consent_records || []).map(r => ({ ...r, source: `${r.action === "granted" ? "Granted" : "Withdrawn"} by ${r.recorded_by_name}`, date: r.timestamp, type: "consent_record" })); return [...prev.filter(p => p.patient_id !== patientId), ...dedicated].sort((a,b) => new Date(b.date) - new Date(a.date)); }); }
    } catch (e) { console.error(e); }
  };

  // Doctor contact CRUD
  const handleSaveContact = async () => {
    if (!contactForm.patient_id || !contactForm.doctor_name) { toast.error("Patient and doctor name are required."); return; }
    setSavingContact(true);
    try {
      const url = editingContact ? `${API}/staff/doctor-contacts/${editingContact}` : `${API}/staff/doctor-contacts`;
      const res = await fetch(url, {
        method: editingContact ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(contactForm),
      });
      if (res.ok) { toast.success(editingContact ? "Contact updated." : "Contact added."); resetContactForm(); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed."); }
    } catch { toast.error("Connection error."); }
    finally { setSavingContact(false); }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      const res = await fetch(`${API}/staff/doctor-contacts/${contactId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Contact deactivated."); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const startEditContact = (c) => {
    setEditingContact(c.contact_id);
    setContactForm({ patient_id: c.patient_id, doctor_name: c.doctor_name, doctor_email: c.doctor_email || "", doctor_phone: c.doctor_phone || "", doctor_practice: c.doctor_practice || "", notes: c.notes || "" });
    setShowContactForm(true);
  };

  const resetContactForm = () => { setShowContactForm(false); setEditingContact(null); setContactForm({ patient_id: "", doctor_name: "", doctor_email: "", doctor_phone: "", doctor_practice: "", notes: "" }); };

  // Consent management
  const handleConsentAction = async () => {
    if (!showConsentForm) return;
    setSavingConsent(true);
    try {
      const url = consentAction === "grant" ? `${API}/staff/consent/grant` : `${API}/staff/consent/withdraw`;
      const body = consentAction === "grant"
        ? { patient_id: showConsentForm, consent_type: consentForm.consent_type, scope: consentForm.scope, method: consentForm.method, notes: consentForm.notes, doctor_contact_id: consentForm.doctor_contact_id }
        : { patient_id: showConsentForm, consent_type: consentForm.consent_type, reason: consentForm.notes, method: consentForm.method, doctor_contact_id: consentForm.doctor_contact_id };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.pending_reports_blocked > 0) toast.warning(`${data.pending_reports_blocked} pending doctor report(s) blocked.`);
        setShowConsentForm(null);
        setConsentForm({ consent_type: "doctor_sharing", scope: "", method: "verbal", notes: "", doctor_contact_id: "" });
        fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || "Failed."); }
    } catch { toast.error("Connection error."); }
    finally { setSavingConsent(false); }
  };

  // Combine all contacts for display
  const allContacts = [
    ...contacts.map(c => ({ ...c, source: "staff" })),
    ...intakeDoctors.filter(d => !contacts.some(c => c.patient_id === d.patient_id)).map(d => ({ ...d, source: "intake", patient_name: d.full_name, consent_given: d.doctor_sharing_consent })),
  ];

  const filteredContacts = search
    ? allContacts.filter(c => (c.patient_name || c.full_name || "").toLowerCase().includes(search.toLowerCase()) || (c.doctor_name || "").toLowerCase().includes(search.toLowerCase()))
    : allContacts;

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="doctor-comms-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Doctor Communication and Consent</h1>
          <p className="mt-1 text-base text-stone-500">Manage doctor contacts, record consent, and track consent changes.</p>
        </div>
        <Button onClick={() => { resetContactForm(); setShowContactForm(true); }} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="add-doctor-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add Doctor Contact
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg">
          <TabsTrigger value="contacts" className="rounded-md text-sm" data-testid="tab-contacts">Doctor Contacts ({allContacts.length})</TabsTrigger>
          <TabsTrigger value="consent-mgmt" className="rounded-md text-sm" data-testid="tab-consent-mgmt">Consent Management</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-md text-sm" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Add/Edit Doctor Contact Form */}
      {showContactForm && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-3" data-testid="contact-form">
          <h3 className="font-bold text-stone-900">{editingContact ? "Edit doctor contact" : "Add doctor contact"}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Select value={contactForm.patient_id} onValueChange={v => setContactForm({ ...contactForm, patient_id: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Doctor's name" value={contactForm.doctor_name} onChange={e => setContactForm({ ...contactForm, doctor_name: e.target.value })} className="rounded-lg" data-testid="doctor-name-input" />
            <Input placeholder="Doctor's email" value={contactForm.doctor_email} onChange={e => setContactForm({ ...contactForm, doctor_email: e.target.value })} className="rounded-lg" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input placeholder="Phone" value={contactForm.doctor_phone} onChange={e => setContactForm({ ...contactForm, doctor_phone: e.target.value })} className="rounded-lg" />
            <Input placeholder="Practice / Institution" value={contactForm.doctor_practice} onChange={e => setContactForm({ ...contactForm, doctor_practice: e.target.value })} className="rounded-lg" />
            <Input placeholder="Notes (optional)" value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} className="rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveContact} disabled={savingContact} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg">
              {savingContact ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {editingContact ? "Update" : "Add Contact"}
            </Button>
            <Button variant="ghost" onClick={resetContactForm}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Consent Grant/Withdraw Form */}
      {showConsentForm && (
        <Card className={`rounded-xl p-5 space-y-3 ${consentAction === "withdraw" ? "border-red-300 bg-red-50/30" : "border-green-300 bg-green-50/30"}`} data-testid="consent-form">
          <h3 className="font-bold text-stone-900">{consentAction === "grant" ? "Record consent" : "Record consent withdrawal"}</h3>
          <div className="flex gap-2 mb-2">
            <Button size="sm" variant={consentAction === "grant" ? "default" : "outline"} className={`rounded-lg text-xs ${consentAction === "grant" ? "bg-green-600 text-white" : ""}`} onClick={() => setConsentAction("grant")}>
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Grant Consent
            </Button>
            <Button size="sm" variant={consentAction === "withdraw" ? "default" : "outline"} className={`rounded-lg text-xs ${consentAction === "withdraw" ? "bg-red-600 text-white" : ""}`} onClick={() => setConsentAction("withdraw")}>
              <ShieldOff className="w-3.5 h-3.5 mr-1" /> Withdraw Consent
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Select value={consentForm.consent_type} onValueChange={v => setConsentForm({ ...consentForm, consent_type: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="service">Service (programme participation)</SelectItem>
                <SelectItem value="doctor_sharing">Doctor report sharing</SelectItem>
                <SelectItem value="marketing">Marketing communications</SelectItem>
              </SelectContent>
            </Select>
            <Select value={consentForm.method} onValueChange={v => setConsentForm({ ...consentForm, method: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="verbal">Verbal</SelectItem>
                <SelectItem value="written">Written</SelectItem>
                <SelectItem value="digital_form">Digital form</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder={consentAction === "grant" ? "Scope (e.g. share with Dr. Smith)" : "Reason for withdrawal"} value={consentForm.notes} onChange={e => setConsentForm({ ...consentForm, notes: e.target.value })} className="rounded-lg" data-testid="consent-notes" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleConsentAction} disabled={savingConsent} className={`rounded-lg ${consentAction === "withdraw" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"} text-white`} data-testid="confirm-consent-btn">
              {savingConsent ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {consentAction === "grant" ? "Confirm Consent" : "Confirm Withdrawal"}
            </Button>
            <Button variant="ghost" onClick={() => setShowConsentForm(null)}>Cancel</Button>
          </div>
          {consentAction === "withdraw" && (
            <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Withdrawing doctor-sharing consent will block all pending doctor report deliveries for this patient.</p>
          )}
        </Card>
      )}

      {/* TAB: Contacts */}
      {tab === "contacts" && (
        <div className="space-y-3">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Search by patient or doctor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-lg" />
          </div>

          {filteredContacts.length > 0 ? (
            <Card className="rounded-xl border-stone-200/60 overflow-hidden">
              <div className="hidden sm:grid grid-cols-[1.5fr_1.5fr_1.2fr_1fr_1fr_0.8fr] gap-3 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
                <span>Patient</span><span>Doctor</span><span>Practice</span><span>Email</span><span>Consent</span><span>Actions</span>
              </div>
              <div className="divide-y divide-stone-100">
                {filteredContacts.map((c, i) => (
                  <div key={c.contact_id || `i-${i}`} className="px-5 py-3 sm:grid sm:grid-cols-[1.5fr_1.5fr_1.2fr_1fr_1fr_0.8fr] sm:gap-3 sm:items-center">
                    <p className="text-sm font-medium text-stone-900 cursor-pointer hover:text-[#FF7A00]" onClick={() => navigate(`/admin/patients/${c.patient_id}`)}>{c.patient_name || c.full_name}</p>
                    <p className="text-sm text-stone-700">{c.doctor_name}</p>
                    <p className="text-sm text-stone-500 hidden sm:block truncate">{c.doctor_practice || "N/A"}</p>
                    <p className="text-sm text-stone-500 hidden sm:block truncate">{c.doctor_email || "N/A"}</p>
                    {c.consent_given ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200 text-xs"><ShieldCheck className="w-3 h-3 mr-1" /> Granted</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-stone-400"><ShieldOff className="w-3 h-3 mr-1" /> None</Badge>
                    )}
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setShowConsentForm(c.patient_id); setConsentForm({ ...consentForm, doctor_contact_id: c.contact_id || "" }); }} data-testid={`manage-consent-${c.contact_id || i}`}>
                        <ShieldCheck className="w-3.5 h-3.5 text-[#FF7A00]" />
                      </Button>
                      {c.source === "staff" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEditContact(c)}><Edit2 className="w-3.5 h-3.5 text-stone-400" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteContact(c.contact_id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <Mail className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No doctor contacts recorded yet.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Consent Management */}
      {tab === "consent-mgmt" && (
        <div className="space-y-4">
          <Card className="rounded-xl border-stone-200/60 p-5">
            <h3 className="text-sm font-bold text-stone-900 mb-1">Consent overview</h3>
            <p className="text-xs text-stone-500">View and manage consent status for each patient. Separate permissions are tracked for: service participation, doctor report sharing, and marketing communications.</p>
          </Card>

          {consentRecords.length > 0 ? (
            <div className="space-y-2">
              {consentRecords.map((c, i) => (
                <Card key={c.consent_id || i} className="rounded-xl border-stone-200/60 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900">{c.patient_name || c.full_name || c.patient_id}</p>
                      <p className="text-xs text-stone-400">{c.source} &middot; {c.date?.split("T")[0]}
                        {c.action && <> &middot; <span className={c.action === "granted" ? "text-green-600" : "text-red-500"}>{c.action}</span></>}
                        {c.consent_type && <> &middot; <span className="capitalize">{c.consent_type?.replace("_", " ")}</span></>}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {c.service_consent && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Service</Badge>}
                      {c.doctor_sharing_consent && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Doctor sharing</Badge>}
                      {c.marketing_consent && <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Marketing</Badge>}
                      {c.action === "granted" && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> {c.consent_type?.replace("_", " ")}</Badge>}
                      {c.action === "withdrawn" && <Badge className="bg-red-50 text-red-700 border-red-200 text-xs"><ShieldOff className="w-3 h-3 mr-1" /> Withdrawn</Badge>}
                    </div>
                    <Button size="sm" variant="ghost" className="shrink-0" onClick={() => { setShowConsentForm(c.patient_id); }} data-testid={`consent-action-${i}`}>
                      <ShieldCheck className="w-4 h-4 text-[#FF7A00]" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <ShieldCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No consent records yet. Grant or withdraw consent using the doctor contacts tab.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Audit Trail */}
      {tab === "audit" && (
        <div className="space-y-4">
          <Card className="rounded-xl border-stone-200/60 p-5">
            <h3 className="text-sm font-bold text-stone-900 mb-1">Consent audit trail</h3>
            <p className="text-xs text-stone-500 mb-3">Every consent change is immutably recorded. Search by patient to view their full consent history.</p>
            <div className="flex gap-2">
              <Select onValueChange={v => { if (v) fetchConsentHistory(v); }}>
                <SelectTrigger className="rounded-lg flex-1"><SelectValue placeholder="Select a patient to view history" /></SelectTrigger>
                <SelectContent>
                  {patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {auditTrail.length > 0 ? (
            <Card className="rounded-xl border-stone-200/60 overflow-hidden">
              <div className="px-5 py-3 bg-stone-50 border-b border-stone-100">
                <h4 className="text-sm font-bold text-stone-900">Audit events ({auditTrail.length})</h4>
              </div>
              <div className="divide-y divide-stone-100">
                {auditTrail.map(ev => (
                  <div key={ev.event_id} className="px-5 py-3 flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${ev.event_type.includes("granted") ? "bg-green-100" : ev.event_type.includes("withdrawn") ? "bg-red-100" : "bg-stone-100"}`}>
                      {ev.event_type.includes("granted") ? <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> : ev.event_type.includes("withdrawn") ? <ShieldOff className="w-3.5 h-3.5 text-red-500" /> : <History className="w-3.5 h-3.5 text-stone-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-stone-700">{ev.details}</p>
                      <p className="text-xs text-stone-400">{ev.user_name} &middot; {ev.timestamp?.split("T")[0]} {ev.timestamp?.split("T")[1]?.split(".")[0]}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-8 text-center">
              <History className="w-8 h-8 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-400">Select a patient above to view their consent audit history.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorCommsPage;
